/**
 * Page-margin letterhead for StudyDoc print.
 *
 * Chrome lays out `position: fixed` print elements inside the page area, so a
 * fixed logo overlaps the first lines of every sheet after the first. The
 * `@page` margin boxes (`@top-left`, `@bottom-left`) sit outside the page
 * area and repeat on every sheet, but they only take `content`, and an image
 * there renders at its intrinsic size. So the logo is wrapped in a small SVG
 * whose intrinsic size is the letterhead size, with the raster embedded as a
 * data URL (an SVG used as an image cannot load external resources).
 */

export const PRINT_LOGO_MAX_HEIGHT_PX = 28
export const PRINT_LOGO_MAX_WIDTH_PX = 120

export interface PrintLogoBox {
  width: number
  height: number
}

/** Fit the logo inside the letterhead box, keeping its aspect ratio. */
export function fitPrintLogo(naturalWidth: number, naturalHeight: number): PrintLogoBox | null {
  if (!(naturalWidth > 0) || !(naturalHeight > 0)) {
    return null
  }
  const scale = Math.min(
    PRINT_LOGO_MAX_HEIGHT_PX / naturalHeight,
    PRINT_LOGO_MAX_WIDTH_PX / naturalWidth,
  )
  return {
    width: Math.round(naturalWidth * scale * 100) / 100,
    height: Math.round(naturalHeight * scale * 100) / 100,
  }
}

function escapeXmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

function escapeCssString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, ' ')
}

export function buildPrintLogoSvgUrl(
  imageDataUrl: string,
  box: PrintLogoBox,
  filter: string | null,
): string {
  const style = filter ? ` style="filter:${escapeXmlAttribute(filter)}"` : ''
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${box.width}" height="${box.height}" viewBox="0 0 ${box.width} ${box.height}">` +
    `<image href="${escapeXmlAttribute(imageDataUrl)}" width="${box.width}" height="${box.height}" preserveAspectRatio="xMinYMid meet"${style}/>` +
    `</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

export function buildPrintMarginBoxCss(options: { logoUrl: string | null; credit: string }): string {
  const { logoUrl, credit } = options
  const logoRule = logoUrl
    ? `@top-left { content: url("${logoUrl}"); vertical-align: bottom; padding-bottom: 5mm; }`
    : ''
  const creditRule = `@bottom-left { content: "${escapeCssString(credit)}"; font-family: Arial, Helvetica, sans-serif; font-weight: 400; font-size: 9pt; vertical-align: top; padding-top: 5mm; }`
  return `@media print { @page { ${logoRule} ${creditRule} } }`
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

function measureImage(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = () => reject(new Error('logo did not load'))
    image.src = src
  })
}

/** Resolve the margin-box logo URL, or null when the logo cannot be embedded. */
export async function loadPrintLogoUrl(src: string, filter: string | null): Promise<string | null> {
  try {
    const response = await fetch(src, { mode: 'cors', credentials: 'omit' })
    if (!response.ok) {
      return null
    }
    const dataUrl = await readAsDataUrl(await response.blob())
    const natural = await measureImage(dataUrl)
    const box = fitPrintLogo(natural.width, natural.height)
    return box ? buildPrintLogoSvgUrl(dataUrl, box, filter) : null
  } catch {
    return null
  }
}
